import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useSearchParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import BudgetBar from "./BudgetBar";
import BudgetDetailPanel from "./BudgetDetail";
import BudgetDialog from "./BudgetDialog";
import {
  BudgetDetail, BudgetDraft, BudgetListResponse, BudgetSummary, OptionsTree, STATUS_COLOR, STATUS_TEXT,
  budgetApi, dayLabel, draftFromDetail, emptyDraft, fmtBudget, fmtUsd, sortBudgets, todayIso,
} from "./budgets";

function BudgetRow({ budget, selected, busy, onSelect, onToggleFollow }: {
  budget: BudgetSummary; selected: boolean; busy: boolean; onSelect: () => void; onToggleFollow: () => void;
}) {
  return (
    <Box onClick={onSelect}
      sx={{ px: 1.5, py: 1, cursor: "pointer", borderLeft: "3px solid",
        borderColor: selected ? "primary.main" : "transparent", bgcolor: selected ? "action.selected" : undefined,
        "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>{budget.name}</Typography>
        <Tooltip title={budget.following ? "Stop following" : "Follow: alerts and Monday digest"}>
          <span>
            <IconButton size="small" disabled={busy} sx={{ my: -0.5 }}
              aria-label={budget.following ? `Stop following ${budget.name}` : `Follow ${budget.name}`}
              onClick={(e) => { e.stopPropagation(); onToggleFollow(); }}>
              {budget.following ? <StarIcon fontSize="small" sx={{ color: "#f59e0b" }} /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {fmtUsd(budget.spend_usd)} of {budget.budget_usd === null ? `${fmtBudget(budget.amount_usd)} / month` : fmtBudget(budget.budget_usd)}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: STATUS_TEXT[budget.status], flexShrink: 0 }}>
          {budget.pct === null ? `starts ${dayLabel(budget.start_date)}` : `${Math.round(budget.pct)}%`}
        </Typography>
      </Stack>
      <BudgetBar pct={budget.pct} color={STATUS_COLOR[budget.status]} tick={Math.min(...budget.thresholds)} />
    </Box>
  );
}

export default function BudgetsSection() {
  const auth = useAuth();
  const apiKey = auth.user?.profile?.["custom:api_key"] as string | undefined;
  const idToken = auth.user?.id_token;
  const api = useMemo(() => (apiKey && idToken ? budgetApi(apiKey, idToken) : null), [apiKey, idToken]);
  const [params, setParams] = useSearchParams();
  const selectedId = Number(params.get("budget")) || null;

  const [list, setList] = useState<BudgetListResponse | null>(null);
  const [detail, setDetail] = useState<BudgetDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // The edited budget's id is kept with the dialog, so Save always writes the budget it was opened for.
  const [dialog, setDialog] = useState<{ mode: "new" | "edit"; draft: BudgetDraft; id?: number } | null>(null);
  const [options, setOptions] = useState<OptionsTree | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const select = useCallback((id: number | null) => {
    const next = new URLSearchParams(params);
    if (id === null) next.delete("budget"); else next.set("budget", String(id));
    setParams(next, { replace: true });
  }, [params, setParams]);

  const budgets = sortBudgets(list?.budgets ?? []);
  const latest = budgets.map((b) => b.data_through).filter(Boolean).sort().pop() ?? null;
  // Without ?budget= the most urgent budget is shown; the URL stays as it is.
  const shownId = selectedId ?? budgets[0]?.id ?? null;

  const reloadList = useCallback(async () => {
    if (!api) { setErr("Authentication credentials not available."); return; }
    setLoading(true);
    try { setList(await api.list()); setErr(null); } catch (e) { setErr((e as Error).message); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { reloadList(); }, [reloadList]);

  useEffect(() => {
    // Drop the previous budget at once: its panel (Edit, Follow, Delete) must not act on the next selection.
    setDetail(null);
    if (!api || shownId === null) return;
    let cancelled = false;
    api.get(shownId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) { setDetail(null); setErr((e as Error).message); } });
    return () => { cancelled = true; };
  }, [api, shownId]);

  const openDialog = async (mode: "new" | "edit", draft: BudgetDraft, id?: number) => {
    setDialog({ mode, draft, id });
    if (!options && api) {
      try { setOptions(await api.options()); } catch (e) { setErr((e as Error).message); }
    }
  };

  const save = async (draft: BudgetDraft) => {
    if (!api || !dialog) return;
    const saved = dialog.mode === "new" || dialog.id === undefined
      ? await api.create(draft) : await api.update(dialog.id, draft);
    setDialog(null);
    setDetail(saved);
    select(saved.id);
    await reloadList();
  };

  const toggleFollow = async (id: number, on: boolean) => {
    if (!api) return;
    setBusy(true);
    try {
      const following = await api.follow(id, on);
      setList((l) => l && { ...l, budgets: l.budgets.map((b) => (b.id === id
        ? { ...b, following, followers: b.followers + (following === b.following ? 0 : following ? 1 : -1) } : b)) });
      if (detail?.id === id) {
        const fresh = await api.get(id);
        setDetail((current) => (current?.id === id ? fresh : current));
      }
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!api || !detail) return;
    setBusy(true);
    try { await api.remove(detail.id); setConfirmDelete(false); setDetail(null); select(null); await reloadList(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ flex: "0 0 auto" }}>
        <Stack>
          <Typography variant="h5">Budgets</Typography>
          <Typography variant="caption" color="text.secondary">This month · data through {dayLabel(latest)}</Typography>
        </Stack>
        <Button variant="contained" onClick={() => openDialog("new", emptyDraft(todayIso()))}>+ New budget</Button>
      </Stack>

      <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "auto", pr: 0.5 }}>
        {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}
        {loading && !list && <CircularProgress size={24} />}

        {list && budgets.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No budgets yet. Create one to get an email when a customer's monthly costs reach a threshold.
            </Typography>
          </Paper>
        )}

        {budgets.length > 0 && (
          <Box sx={{ display: "grid", gap: 2, alignItems: "start",
            gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" } }}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              {budgets.map((b, i) => (
                <Box key={b.id} sx={i ? { borderTop: "1px solid", borderColor: "divider" } : undefined}>
                  <BudgetRow budget={b} selected={b.id === shownId} busy={busy} onSelect={() => select(b.id)}
                    onToggleFollow={() => toggleFollow(b.id, !b.following)} />
                </Box>
              ))}
            </Paper>
            {detail ? (
              <BudgetDetailPanel budget={detail} busy={busy}
                onToggleFollow={() => toggleFollow(detail.id, !detail.following)}
                onEdit={() => openDialog("edit", draftFromDetail(detail), detail.id)}
                onDelete={() => setConfirmDelete(true)} />
            ) : (
              !err && <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>
            )}
          </Box>
        )}
      </Box>

      {dialog && api && (
        <BudgetDialog open mode={dialog.mode} initial={dialog.draft} options={options}
          loadPreview={(scope, amount, start) => api.preview(scope, amount, start)}
          onSave={save} onClose={() => setDialog(null)} />
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete {detail?.name}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The budget, its followers and its alert history are removed. Nobody gets mails about it any more.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={remove}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
